import * as THREE from "three";
import AppData from "../../app.data";
import CameraService from "../../services/camera/camera.service";
import PhysicsService from "../../services/physics/physics.service";
import { SceneUtils } from "../scene.utils";

const appData = new AppData();

export class AnimationLoop {
    cameraService: CameraService;
    physicsService: PhysicsService;
    sceneUtils: SceneUtils;
    clock: THREE.Clock;
    renderer: THREE.WebGLRenderer;
    
    // Callbacks
    addToRenderQueueCallback: () => void;
    renderCallback: (delta: number) => void;
    
    // State
    private newCameraPosition = new THREE.Vector3();
    private oldCameraPosition = new THREE.Vector3();
    private newCameraRotation = new THREE.Vector3();
    private oldCameraRotation = new THREE.Vector3();
    private minimumRotationDelta = 0.00000001;
    private minimumPositionDelta = 0.000003;

    constructor(
        cameraService: CameraService, 
        physicsService: PhysicsService, 
        sceneUtils: SceneUtils,
        renderer: THREE.WebGLRenderer,
        clock: THREE.Clock,
        addToRenderQueueCallback: () => void,
        renderCallback: (delta: number) => void
    ) {
        this.cameraService = cameraService;
        this.physicsService = physicsService;
        this.sceneUtils = sceneUtils;
        this.renderer = renderer;
        this.clock = clock;
        this.addToRenderQueueCallback = addToRenderQueueCallback;
        this.renderCallback = renderCallback;
    }

    get camSpeed() {
        return this.cameraService.camSpeed;
    }

    public start() {
        this.renderer.setAnimationLoop(this.animate.bind(this));
    }

    public stop() {
        if (this.renderer) {
            this.renderer.setAnimationLoop(null);
        }
    }

    private animate() {
        const delta = this.clock.getDelta();
        const timeScale = delta * 60;
        const isPresentingVR = this.renderer.xr.isPresenting;
        const hasInput = Object.keys(appData.keyStrokes).length > 0;
        const hasPhysicsMotion = this.physicsService.hasActiveMotion?.() ?? true;
        const renderService = appData.renderService as any;
        const hasQueuedRender = renderService?.hasQueuedFrames?.() ?? true;
        const hasContinuousWork = renderService?.hasActiveContinuousWork?.() ?? true;

        if (!isPresentingVR && !hasInput && !hasPhysicsMotion && !hasQueuedRender && !hasContinuousWork && !appData.viewerRotationActive) {
            return;
        }

        this.handleInput(timeScale);
        if (isPresentingVR || hasPhysicsMotion || hasInput) {
            this.handlePhysics(delta);
        }
        const cameraDependentRefreshNeeded = this.checkCameraMovement(hasQueuedRender);

        if (!isPresentingVR && cameraDependentRefreshNeeded && appData.emissionModels.length > 0) {
            this.sceneUtils.interpolateModelsEmissionBasedOnCameraPosition(appData.camera, appData.emissionModels, 5);
        }

        this.renderCallback(delta);

        this.oldCameraPosition.copy(this.newCameraPosition);
        this.oldCameraRotation.copy(this.newCameraRotation);
    }

    private handleInput(timeScale: number) {
        if (Object.keys(appData.keyStrokes).length > 0) {
            this.cameraService.camSpeed = 0.3;
            
            for (const property in appData.keyStrokes) {
                switch (appData.keyStrokes[property]) {
                    case "a": case "A": case "ArrowLeft":
                        this.cameraService.controlMoveRight(-this.camSpeed * timeScale);
                        break;
                    case "w": case "W": case "ArrowUp":
                        this.cameraService.controlMoveForward(this.camSpeed * timeScale);
                        break;
                    case "d": case "D": case "ArrowRight":
                        this.cameraService.controlMoveRight(this.camSpeed * timeScale);
                        break;
                    case "s": case "S": case "ArrowDown":
                        this.cameraService.controlMoveForward(-this.camSpeed * timeScale);
                        break;
                    case "Shift":
                        this.cameraService.camSpeed = 0.6;
                        break;
                }
            }
        }
    }

    private handlePhysics(delta: number) {
        const STEPS_PER_FRAME = this.renderer.xr.isPresenting ? 5 : 120;
        if (!this.renderer.xr.isPresenting && !this.physicsService.hasActiveMotion?.()) {
            return;
        }
        const physicsDelta = Math.min(0.05, delta) / STEPS_PER_FRAME;

        for (let i = 0; i < STEPS_PER_FRAME; i++) {
            if (this.physicsService.colideSet) this.physicsService.updatePlayer(physicsDelta);
        }
    }

    private checkCameraMovement(forceCameraDependentRefresh = false) {
        appData.camera.getWorldPosition(this.newCameraPosition);
        this.newCameraRotation.set(appData.camera.rotation.x, appData.camera.rotation.y, appData.camera.rotation.z);

        const lastDistanceTraveled = this.newCameraPosition.distanceTo(this.oldCameraPosition);
        const lastRotationTraveled = Math.max(
            Math.abs(this.newCameraRotation.x - this.oldCameraRotation.x),
            Math.abs(this.newCameraRotation.y - this.oldCameraRotation.y),
            Math.abs(this.newCameraRotation.z - this.oldCameraRotation.z)
        );
        const cameraMoved = lastDistanceTraveled > this.minimumPositionDelta || lastRotationTraveled > this.minimumRotationDelta;

        if (cameraMoved || forceCameraDependentRefresh) {
            this.sceneUtils.rotateArtworksBasedOnCameraPosition(this.newCameraPosition);
        }
        if (cameraMoved) {
            this.addToRenderQueueCallback();
        }
        return cameraMoved || forceCameraDependentRefresh;
    }
}

